import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faEye,
  faEyeSlash,
  faCheck,
  faShieldAlt,
  faKey,
  faCheckCircle,
  faExclamationTriangle,
  faHome,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from "../../Helpers/toastHelper";
import { storageHelper } from "../../Helpers/storageHelper";
import MiniLoader from "../Shared/MiniLoader";
import extractErrorMessage from "../../Helpers/APIHelper";
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ChangeUserPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Update password strength for new password
    if (name === "new_password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Check password match
    if (name === "confirm_password" || name === "new_password") {
      const newPassword = name === "new_password" ? value : formData.new_password;
      const confirmPassword = name === "confirm_password" ? value : formData.confirm_password;

      if (confirmPassword) {
        setPasswordMatch(newPassword === confirmPassword);
      } else {
        setPasswordMatch(null);
      }
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords match
    if (formData.new_password !== formData.confirm_password) {
      toastHelper.error("كلمة المرور الجديدة وتأكيدها غير متطابقتين");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (passwordStrength < 50) {
      toastHelper.error("كلمة المرور ضعيفة جداً، يرجى اختيار كلمة مرور أقوى");
      setLoading(false);
      return;
    }

    try {
      await ApiServices.put("User/ChangePassword", formData);
      setIsSuccess(true);
      toastHelper.success("تم تغيير كلمة المرور بنجاح");

      // Clear all storage to force re-authentication
      storageHelper.clearStorage();

      // Navigate to login page after showing success animation
      setTimeout(() => {
        navigate("/signin");
      }, 2000);
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Success Animation Component
  const SuccessAnimation = () => (
    <motion.div
      className="cp-success-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="cp-success-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
      >
        <div className="cp-success-icon-wrap">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
        <h3>تم تغيير كلمة المرور!</h3>
        <p>سيتم توجيهك إلى صفحة تسجيل الدخول في ثوانٍ...</p>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        <title>تغيير كلمة المرور - منصة امتحاني</title>
      </Helmet>
      <Header />

      <section className="cp-root">
        <div className="cp-container">

          {/* Header Section */}
          <motion.div
            className="cp-header"
            initial="hidden"
            animate="visible"
          >
            <motion.div className="cp-eyebrow" variants={fadeUp} custom={0}>
              <span className="cp-eyebrow-dot" />
              أمان الحساب
            </motion.div>
            <motion.h2 className="cp-title" variants={fadeUp} custom={1}>
              تغيير كلمة <em>المرور</em>
            </motion.h2>
            <motion.p className="cp-subtitle" variants={fadeUp} custom={2}>
              حافظ على أمان حسابك من خلال تحديث كلمة المرور الخاصة بك بانتظام.
              <br /> تأكد من استخدام مزيج قوي من الأحرف والأرقام.
            </motion.p>
          </motion.div>

          {/* Form Card */}
          <motion.div
            className="cp-card-wrapper"
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="cp-card"
              variants={fadeUp}
              custom={3}
            >
              <div className="cp-card-glow" />

              <form onSubmit={handleSubmit} className="cp-form">
                {/* Current Password */}
                <div className={`cp-form-group ${focusedField === 'current' ? 'focused' : ''}`}>
                  <label className="cp-label">
                    <div className="cp-label-icon">
                      <FontAwesomeIcon icon={faKey} />
                    </div>
                    <span>كلمة المرور الحالية</span>
                  </label>
                  <div className="cp-input-wrap">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      name="current_password"
                      value={formData.current_password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('current')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="أدخل كلمة المرور الحالية"
                      className="cp-input"
                    />
                    <button
                      type="button"
                      className="cp-toggle-btn"
                      onClick={() => togglePasswordVisibility("current")}
                    >
                      <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className={`cp-form-group ${focusedField === 'new' ? 'focused' : ''}`}>
                  <label className="cp-label">
                    <div className="cp-label-icon" style={{ color: '#0891b2', background: 'rgba(8,145,178,0.1)' }}>
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                    <span>كلمة المرور الجديدة</span>
                  </label>
                  <div className="cp-input-wrap">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('new')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="أدخل كلمة المرور الجديدة"
                      className="cp-input"
                    />
                    <button
                      type="button"
                      className="cp-toggle-btn"
                      onClick={() => togglePasswordVisibility("new")}
                    >
                      <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
                    </button>
                  </div>

                  {/* Strength Indicator */}
                  <AnimatePresence>
                    {formData.new_password && (
                      <motion.div
                        className="cp-strength-box"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <div className="cp-strength-bar-bg">
                          <motion.div
                            className="cp-strength-bar-fill"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${passwordStrength}%`,
                              backgroundColor: passwordStrength < 50 ? "#ef4444" : passwordStrength < 75 ? "#6366f1" : "#10b981"
                            }}
                          />
                        </div>
                        <span className="cp-strength-text" style={{
                          color: passwordStrength < 50 ? "#ef4444" : passwordStrength < 75 ? "#6366f1" : "#10b981"
                        }}>
                          {passwordStrength < 25 ? "ضعيف جداً" : passwordStrength < 50 ? "ضعيف" : passwordStrength < 75 ? "جيد" : "قوي جداً"}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Confirm Password */}
                <div className={`cp-form-group ${focusedField === 'confirm' ? 'focused' : ''} ${passwordMatch === false ? 'has-error' : passwordMatch === true ? 'is-valid' : ''}`}>
                  <label className="cp-label">
                    <div className="cp-label-icon" style={{ color: '#9333ea', background: 'rgba(147,51,234,0.1)' }}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <span>تأكيد كلمة المرور</span>
                  </label>
                  <div className="cp-input-wrap">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('confirm')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="أعد إدخال كلمة المرور"
                      className="cp-input"
                    />
                    <button
                      type="button"
                      className="cp-toggle-btn"
                      onClick={() => togglePasswordVisibility("confirm")}
                    >
                      <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                    </button>
                    {passwordMatch !== null && (
                      <div className="cp-match-icon">
                        <FontAwesomeIcon
                          icon={passwordMatch ? faCheckCircle : faExclamationTriangle}
                          color={passwordMatch ? "#10b981" : "#ef4444"}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="cp-actions">
                  <motion.button
                    type="submit"
                    className="cp-submit-btn"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={loading || passwordStrength < 50 || passwordMatch === false}
                  >
                    {loading ? (
                      <MiniLoader size={20} color="#ffffff" variant="spinner" />
                    ) : (
                      <>
                        <span>تحديث كلمة المرور</span>
                        <FontAwesomeIcon icon={faStar} />
                      </>
                    )}
                  </motion.button>

                  <motion.button
                    type="button"
                    className="cp-back-btn"
                    onClick={() => navigate("/")}
                    whileHover={{ x: -4 }}
                  >
                    <FontAwesomeIcon icon={faHome} />
                    <span>العودة للرئيسية</span>
                  </motion.button>
                </div>
              </form>

              <div className="cp-card-bar" />
            </motion.div>
          </motion.div>

        </div>
        <Footer />
      </section>

      <AnimatePresence>
        {isSuccess && <SuccessAnimation />}
      </AnimatePresence>

      <style>{`
        .cp-root {
          --gold: #6366f1;
          --teal: #0891b2;
          --lavender: #9333ea;
          --bg: #f8fafc;
          --surface: #fff;
          --text: #0f172a;
          --muted: #64748b;
          --border: rgba(15,23,42,0.08);
          
          font-family: "Zain", sans-serif;
          direction: rtl;
          min-height: 100vh;
          padding: 140px 0 100px;
          background: var(--bg);
          position: relative;
          overflow: hidden;
        }

        .cp-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        /* ── Header ── */
        .cp-header { text-align: center; margin-bottom: 50px; }
        .cp-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: var(--gold); font-weight: 800; font-size: 0.95rem;
          letter-spacing: 0.1em; text-transform: uppercase;
          margin-bottom: 20px;
        }
        .cp-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: var(--gold);
          animation: cp-pulse 2.2s ease-in-out infinite;
        }
        @keyframes cp-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .cp-title {
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          font-weight: 900; color: var(--text); margin: 0 0 16px;
          line-height: 1.2;
        }
        .cp-title em {
          font-style: normal;
          background: linear-gradient(90deg, var(--gold) 0%, #6366f1 60%, var(--gold) 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .cp-subtitle {
          font-size: 1.15rem; color: var(--muted); line-height: 1.7; margin: 0;
        }

        /* ── Card ── */
        .cp-card-wrapper {
          max-width: 550px;
          margin: 0 auto;
        }
        .cp-card {
          position: relative;
          background: var(--surface);
          border-radius: 30px;
          border: 1px solid var(--border);
          padding: 45px;
          box-shadow: 0 20px 50px rgba(15,23,42,0.05);
          overflow: hidden;
        }
        .cp-card-glow {
          position: absolute; inset: -100px;
          background: radial-gradient(circle at center, rgba(232,146,10,0.05) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .cp-card-bar {
          position: absolute; bottom: 0; left: 0; width: 100%; height: 4px;
          background: linear-gradient(90deg, var(--gold), var(--teal), var(--lavender));
          opacity: 0.8;
        }

        /* ── Form ── */
        .cp-form {
          position: relative; z-index: 1;
          display: flex; flex-direction: column; gap: 28px;
        }
        .cp-form-group {
          display: flex; flex-direction: column; gap: 10px;
          transition: all 0.3s ease;
        }
        .cp-label {
          display: flex; align-items: center; gap: 12px;
          font-size: 1.05rem; font-weight: 700; color: var(--text);
        }
        .cp-label-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(232,146,10,0.1);
          display: flex; align-items: center; justify-content: center;
          color: var(--gold); font-size: 0.9rem;
        }
        .cp-input-wrap {
          position: relative;
        }
        .cp-input {
          width: 100%;
          padding: 14px 20px;
          padding-left: 50px;
          border-radius: 16px;
          border: 1.5px solid var(--border);
          background: #fcfcfd;
          font-family: inherit;
          font-size: 1.05rem;
          color: var(--text);
          transition: all 0.3s ease;
        }
        .cp-input:focus {
          outline: none;
          border-color: var(--gold);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(232,146,10,0.05);
        }
        .cp-form-group.is-valid .cp-input {
          border-color: #10b981;
          padding-right: 48px;
        }
        .cp-form-group.is-valid .cp-input:focus {
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
        }
        .cp-form-group.has-error .cp-input {
          border-color: #ef4444;
          padding-right: 48px;
        }
        .cp-form-group.has-error .cp-input:focus {
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
        }
        .cp-toggle-btn {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          background: none; border: none; color: #94a3b8;
          cursor: pointer; padding: 6px; font-size: 1.1rem;
          transition: color 0.2s;
        }
        .cp-toggle-btn:hover { color: var(--gold); }
        
        .cp-match-icon {
          position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
          font-size: 1.2rem;
          display: flex; align-items: center; justify-content: center;
          pointer-events: none;
        }

        /* ── Strength ── */
        .cp-strength-box {
          margin-top: 4px; display: flex; align-items: center; gap: 12px;
        }
        .cp-strength-bar-bg {
          flex: 1; height: 5px; background: #f1f5f9; border-radius: 10px; overflow: hidden;
        }
        .cp-strength-bar-fill {
          height: 100%; border-radius: 10px; transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cp-strength-text {
          font-size: 0.85rem; font-weight: 800; min-width: 60px; text-align: left;
        }

        /* ── Actions ── */
        .cp-actions {
          display: flex; flex-direction: column; gap: 16px; margin-top: 10px;
        }
        .cp-submit-btn {
          display: flex; align-items: center; justify-content: center; gap: 12px;
          background: var(--text); color: #fff; border: none; border-radius: 16px;
          padding: 16px; font-family: inherit; font-weight: 800; font-size: 1.1rem;
          cursor: pointer; transition: all 0.3s;
          box-shadow: 0 10px 25px rgba(15,23,42,0.15);
        }
        .cp-submit-btn:hover:not(:disabled) {
          background: #000; transform: translateY(-2px);
          box-shadow: 0 15px 30px rgba(15,23,42,0.2);
        }
        .cp-submit-btn:disabled {
          opacity: 0.5; cursor: not-allowed; transform: none;
        }
        .cp-back-btn {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          background: transparent; border: 1.5px solid var(--border);
          border-radius: 16px; padding: 14px; font-family: inherit;
          font-weight: 700; font-size: 1rem; color: var(--muted);
          cursor: pointer; transition: all 0.3s;
        }
        .cp-back-btn:hover {
          background: #f1f5f9; color: var(--text); border-color: var(--muted);
        }

        /* ── Success ── */
        .cp-success-overlay {
          position: fixed; inset: 0; background: rgba(255,255,255,0.9);
          backdrop-filter: blur(8px); z-index: 9999;
          display: flex; align-items: center; justify-content: center;
        }
        .cp-success-content {
          text-align: center; background: #fff; padding: 50px;
          border-radius: 40px; box-shadow: 0 30px 70px rgba(0,0,0,0.1);
          max-width: 450px; width: 90%; border: 1px solid var(--border);
        }
        .cp-success-icon-wrap {
          font-size: 4.5rem; color: #10b981; margin-bottom: 24px;
        }
        .cp-success-content h3 { font-size: 2rem; font-weight: 900; color: var(--text); margin-bottom: 12px; }
        .cp-success-content p { color: var(--muted); font-size: 1.1rem; }

        /* ── Responsive ── */
        @media(max-width: 640px) {
          .cp-root { padding: 100px 0 60px; }
          .cp-card { padding: 30px 24px; }
          .cp-title { font-size: 2.2rem; }
        }
      `}</style>
    </>
  );
};

export default ChangeUserPassword;